/**
 * DocumentEditor Component
 * Reusable enhanced editor wrapping WysiwygEditor with:
 * - Fullscreen mode
 * - Search & Replace
 * - AI Refine panel
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Maximize2,
  Minimize2,
  Search,
  Sparkles,
} from 'lucide-react';
import type { Editor } from '@tiptap/react';
import { WysiwygEditor, ToolbarButton } from './WysiwygEditor';
import { SearchReplacePanel } from './SearchReplacePanel';
import { AiRefinePanel } from './AiRefinePanel';
import { useSearchReplace } from './useSearchReplace';

interface DocumentEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  enableFullscreen?: boolean;
  enableSearchReplace?: boolean;
  enableAiRefine?: boolean;
}

export const DocumentEditor: React.FC<DocumentEditorProps> = ({
  value,
  onChange,
  placeholder,
  className = '',
  minHeight = 'min-h-[400px]',
  enableFullscreen = true,
  enableSearchReplace = true,
  enableAiRefine = true,
}) => {
  const { t } = useTranslation();
  const [editor, setEditor] = useState<Editor | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSearchReplace, setShowSearchReplace] = useState(false);
  const [showAiRefine, setShowAiRefine] = useState(false);

  const searchReplace = useSearchReplace(editor);

  // Lock body scroll in fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape exits fullscreen
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
        return;
      }
      // Ctrl+H opens search/replace
      if (e.key === 'h' && (e.ctrlKey || e.metaKey) && enableSearchReplace) {
        e.preventDefault();
        setShowSearchReplace(prev => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, enableSearchReplace]);

  const handleEditorReady = useCallback((ed: Editor) => {
    setEditor(ed);
  }, []);

  const handleApplyRefinement = useCallback((newContent: string) => {
    onChange(newContent);
  }, [onChange]);

  // Toolbar extra buttons
  const toolbarExtra = (
    <>
      {enableSearchReplace && (
        <ToolbarButton
          onClick={() => setShowSearchReplace(prev => !prev)}
          isActive={showSearchReplace}
          title={t('documentEditor.searchReplace.title') + ' (Ctrl+H)'}
        >
          <Search className="w-4 h-4" />
        </ToolbarButton>
      )}
      {enableAiRefine && (
        <ToolbarButton
          onClick={() => setShowAiRefine(prev => !prev)}
          isActive={showAiRefine}
          title={t('documentEditor.aiRefine.title')}
        >
          <Sparkles className="w-4 h-4" />
        </ToolbarButton>
      )}
      {enableFullscreen && (
        <ToolbarButton
          onClick={() => setIsFullscreen(prev => !prev)}
          title={isFullscreen
            ? t('documentEditor.exitFullscreen')
            : t('documentEditor.fullscreen')
          }
        >
          {isFullscreen
            ? <Minimize2 className="w-4 h-4" />
            : <Maximize2 className="w-4 h-4" />
          }
        </ToolbarButton>
      )}
    </>
  );

  const containerClass = isFullscreen
    ? 'fixed inset-0 z-[60] bg-white flex flex-col'
    : className;

  const editorMinHeight = isFullscreen ? 'min-h-0 flex-1' : minHeight;

  return (
    <div className={containerClass}>
      {/* Search & Replace panel */}
      {showSearchReplace && (
        <SearchReplacePanel
          hook={searchReplace}
          onClose={() => setShowSearchReplace(false)}
        />
      )}

      {/* Main editor area */}
      <div className={`flex-1 ${showAiRefine ? 'flex' : ''}`}>
        <div className={showAiRefine ? 'flex-1 min-w-0' : ''}>
          <WysiwygEditor
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            minHeight={editorMinHeight}
            onEditorReady={handleEditorReady}
            toolbarExtra={toolbarExtra}
          />
        </div>

        {/* AI Refine side panel */}
        {showAiRefine && (
          <AiRefinePanel
            isOpen={showAiRefine}
            onClose={() => setShowAiRefine(false)}
            currentContent={value}
            onApplyRefinement={handleApplyRefinement}
          />
        )}
      </div>

      {/* Search highlight styles */}
      <style>{`
        .search-highlight {
          background-color: #fef08a;
          border-radius: 2px;
        }
        .search-highlight-active {
          background-color: #fb923c;
          color: white;
          border-radius: 2px;
        }
      `}</style>
    </div>
  );
};

export default DocumentEditor;
